import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  choisitUnContact,
  environnementContacts,
  estVueWebAndroid,
  lisFicheVCard,
  oublieChampsDuCarnet,
  prepareCarnet,
  rueEtLocalite,
} from '../lib/contacts';
import { _inp } from './ui/Inputs.jsx';
import { C } from '../theme/palette';
import { Btn } from './ui/Btn.jsx';

/**
 * Le réglage de Safari qui ouvre les contacts de l'iPhone depuis un site.
 *
 * <p>Allumé une fois, « Depuis mes contacts » ouvre la même liste « Contacts »
 * que les applications de l'App Store (le patron montrait celle d'Invoice2go),
 * et le client choisi remplit nom, téléphone et e-mail d'un geste.
 */
export const REGLAGE_SAFARI = [
  'Ouvre l’app Réglages de l’iPhone.',
  'Apps → Safari → tout en bas : Avancé.',
  'Feature Flags → active « Contact Picker API ».',
  'Reviens ici et appuie sur « Depuis mes contacts » : tes contacts s’ouvrent.',
];

/**
 * Quand « Remplir autom. le contact » ne paraît pas au-dessus du clavier :
 * Safari ne remplit depuis Contacts qu'une fois ce réglage allumé, avec la
 * fiche du patron désignée comme « Mes infos ».
 */
const AIDE_CLAVIER =
  'Une seule fois : Réglages → Apps → Safari → Remplissage automatique → allume « Utiliser les coordonnées » et choisis ta fiche dans « Mes infos ».';

const FICHE_VIDE = { nom: '', entreprise: '', rue: '', cp: '', ville: '', tel: '', email: '' };

const STYLE_CHAMP = { ..._inp, fontSize: 16, background: '#fff' };

/**
 * Ce que dit la carte quand le carnet ne peut pas s'ouvrir ici, selon le
 * téléphone et le navigateur (voir `environnementContacts`).
 */
const TITRES = {
  'ios-safari':
    'Pour que ce bouton ouvre tes contacts, règle Safari une seule fois (30 secondes) :',
  'ios-autre':
    'Ce navigateur ne peut pas ouvrir les contacts de l’iPhone : Apple ne le permet qu’à Safari. Ouvre swisstech.tech dans Safari (⋯ → « Ouvrir dans Safari »), puis règle une seule fois :',
  'android-autre':
    'Ce navigateur ne sait pas ouvrir les contacts du téléphone. Ouvre swisstech.tech dans Chrome : ce bouton ouvrira directement ton répertoire.',
  'android-vue-web':
    'Tu es dans une application (WhatsApp, Facebook…) qui ne laisse pas ouvrir tes contacts. Touche ⋮ → « Ouvrir dans Chrome » : ce bouton ouvrira directement ton répertoire.',
  ordinateur:
    'Sur un ordinateur, il n’y a pas de répertoire de téléphone. Ouvre SwissTech sur ton téléphone, ou choisis une fiche contact (.vcf) enregistrée ici.',
};

/**
 * Bouton « Depuis mes contacts », commun à tous les formulaires qui saisissent
 * une personne : accueil, clients, nouvelle offre, décharge.
 *
 * <p>Là où le carnet d'adresses peut s'ouvrir — l'application SwissTech,
 * Chrome sur Android, Safari une fois « Contact Picker API » allumé — il
 * s'ouvre au toucher, sans attente intermédiaire : Safari n'ouvre le carnet
 * que dans le geste même du doigt. Ailleurs, une carte dit pourquoi et
 * comment, selon le téléphone et le navigateur : réglage de Safari, passer
 * par Safari ou par Chrome, ou, sur un ordinateur, une fiche `.vcf`. La
 * saisie à la main reste toujours possible, et le formulaire modifiable.
 *
 * @param {object} props propriétés du bouton
 * @param {(contact: {nom: string, adr: string, npa: string, tel: string, email: string}) => void} props.onContact
 *   reçoit le contact choisi, d'où qu'il vienne
 * @param {(message: string) => void} [props.onEchec] reçoit un message à
 *   montrer quand rien n'a pu être lu
 * @param {string} [props.label] libellé du bouton
 * @returns {import('react').ReactElement} le bouton et son champ de fichier
 */
export function BoutonContacts({
  onContact,
  onEchec,
  label = '📇 Depuis mes contacts',
  ...apparence
}) {
  const champ = useRef(null);
  const premier = useRef(null);
  const carte = useRef(null);
  const [aide, setAide] = useState(false);
  const [comment, setComment] = useState(false);
  const [clavier, setClavier] = useState(false);
  const [fiche, setFiche] = useState(FICHE_VIDE);
  const [lieu, setLieu] = useState('carnet');

  // Les champs du carnet se demandent avant le toucher : Safari n'ouvre le
  // carnet que dans le geste même du doigt.
  useEffect(() => {
    prepareCarnet();
  }, []);

  // La carte s'ouvre sous le bouton, que la barre « Générer l'offre » du bas
  // de l'écran recouvre : sans ce défilement, le toucher semblait sans effet.
  useEffect(() => {
    if (aide && carte.current && typeof carte.current.scrollIntoView === 'function') {
      carte.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [aide]);

  // Un vrai champ de formulaire, nommé et marqué pour le remplissage
  // automatique : c'est à ces marques que Safari reconnaît où mettre le nom,
  // la rue, le NPA, la ville, le téléphone et l'e-mail du contact choisi.
  const champAuto = (cle, auto, indice) => ({
    name: auto,
    autoComplete: auto,
    placeholder: indice,
    'aria-label': indice,
    value: fiche[cle],
    onChange: (e) => setFiche((p) => ({ ...p, [cle]: e.target.value })),
    style: STYLE_CHAMP,
  });

  const ferme = () => {
    setAide(false);
    setClavier(false);
    setComment(false);
    setFiche(FICHE_VIDE);
  };

  const utilise = () => {
    const nom = fiche.nom.trim() || fiche.entreprise.trim();
    if (!nom) return;
    const { adr, npa } = rueEtLocalite(fiche.rue, fiche.cp, fiche.ville);
    onContact({ nom, adr, npa, tel: fiche.tel.trim(), email: fiche.email.trim() });
    ferme();
  };

  const ouvre = async () => {
    const env = environnementContacts();
    if (env !== 'natif' && env !== 'carnet') {
      // Le carnet ne s'ouvre pas ici : la carte dit pourquoi et comment,
      // selon le téléphone et le navigateur. L'ancien bouton ouvrait d'office
      // l'app Fichiers : « ça m'envoie ailleurs ».
      setLieu(env === 'android-autre' && estVueWebAndroid() ? 'android-vue-web' : env);
      setAide(true);
      return;
    }
    try {
      // Rien n'est attendu avant l'ouverture du carnet : elle doit rester
      // dans le geste du doigt.
      const contact = await choisitUnContact();
      if (contact) {
        onContact(contact);
      } else if (onEchec) {
        onEchec('Aucun contact sélectionné. Saisis-le ci-dessous.');
      }
    } catch (e) {
      if (e && e.name === 'TypeError') {
        // Un champ refusé par ce carnet : la prochaine fois, les champs sûrs.
        oublieChampsDuCarnet();
        if (onEchec) onEchec('Touche encore une fois « Depuis mes contacts ».');
      } else if (onEchec) {
        onEchec('Import annulé ou non autorisé. Saisis-le ci-dessous.');
      }
    }
  };

  const lit = async (evenement) => {
    const fichier = evenement.target.files && evenement.target.files[0];
    // Le champ est vidé aussitôt : sans cela, choisir deux fois la même fiche
    // ne déclencherait rien la seconde fois.
    evenement.target.value = '';
    if (!fichier) {
      return;
    }
    try {
      onContact(await lisFicheVCard(fichier));
    } catch (e) {
      if (onEchec) onEchec('Fiche illisible. Saisis-la ci-dessous.');
    }
  };

  // Sur iPhone sans carnet : d'abord le réglage de Safari qui ouvre la vraie
  // liste « Contacts », comme dans les applications (Invoice2go). En attendant,
  // un formulaire que Safari remplit depuis Contacts par le clavier. L'ancienne
  // carte envoyait vers l'app Fichiers, où le patron ne trouvait rien.
  const ouvreClavier = () => {
    // Le champ Nom reçoit le curseur dans le même geste que le clic : le
    // clavier s'ouvre aussitôt, avec « Remplir autom. le contact ».
    flushSync(() => setClavier(true));
    if (premier.current) premier.current.focus();
  };
  const surIphone = lieu === 'ios-safari' || lieu === 'ios-autre';
  return (
    <div style={{ flex: aide ? '1 1 100%' : 1, minWidth: 0 }}>
      <Btn label={label} onClick={ouvre} {...apparence} />
      {aide && (
        <div
          ref={carte}
          role="note"
          style={{
            marginTop: 6,
            background: C.tealL,
            border: '1px solid ' + C.teal,
            borderRadius: 10,
            padding: '10px 11px',
            fontSize: 12.5,
            color: C.ink,
            lineHeight: 1.45,
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <b style={{ flex: 1 }}>{TITRES[lieu]}</b>
            <button
              type="button"
              aria-label="Fermer"
              onClick={ferme}
              style={{
                border: 'none',
                background: 'none',
                fontSize: 14,
                color: C.muted,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              ✕
            </button>
          </div>
          {surIphone && (
            <ol style={{ margin: '6px 0 0', paddingLeft: 20 }}>
              {REGLAGE_SAFARI.map((etape) => (
                <li key={etape} style={{ marginBottom: 3 }}>
                  {etape}
                </li>
              ))}
            </ol>
          )}
          {!surIphone && (
            <div style={{ marginTop: 8 }}>
              <Btn
                label="📄 Choisir une fiche contact (.vcf)"
                sm
                ghost
                color={C.teal}
                full
                onClick={() => champ.current && champ.current.click()}
              />
            </div>
          )}
          {!clavier ? (
            <div style={{ marginTop: 8 }}>
              <Btn
                label={
                  surIphone
                    ? '✍️ En attendant : remplir avec le clavier'
                    : '✍️ Saisir le client à la main'
                }
                sm
                ghost
                color={C.teal}
                full
                onClick={ouvreClavier}
              />
            </div>
          ) : (
            <form
              autoComplete="on"
              onSubmit={(e) => {
                e.preventDefault();
                utilise();
              }}
              style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed ' + C.teal }}
            >
              {surIphone && (
                <div>
                  <b>Touche la case Nom</b>, puis au-dessus du clavier :{' '}
                  <b>« Remplir autom. le contact »</b> → <b>« Autre contact »</b>.
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                <input ref={premier} {...champAuto('nom', 'name', 'Nom')} autoCapitalize="words" />
                <input
                  {...champAuto('entreprise', 'organization', 'Entreprise (si c’est une société)')}
                />
                <input {...champAuto('rue', 'address-line1', 'Rue et n°')} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    {...champAuto('cp', 'postal-code', 'NPA')}
                    inputMode="numeric"
                    style={{ ...STYLE_CHAMP, width: 90, flex: '0 0 auto' }}
                  />
                  <input {...champAuto('ville', 'address-level2', 'Ville')} />
                </div>
                <input {...champAuto('tel', 'tel', 'Téléphone')} type="tel" />
                <input {...champAuto('email', 'email', 'E-mail')} type="email" />
              </div>
              <div style={{ marginTop: 8 }}>
                <Btn
                  label="✓ Prendre ce client"
                  color={C.green}
                  full
                  disabled={!(fiche.nom.trim() || fiche.entreprise.trim())}
                  onClick={utilise}
                />
              </div>
              {surIphone && (
                <>
                  <button
                    type="button"
                    aria-expanded={comment}
                    onClick={() => setComment((v) => !v)}
                    style={{
                      marginTop: 7,
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: C.teal,
                      cursor: 'pointer',
                    }}
                  >
                    {comment ? 'Masquer' : 'Le bouton n’apparaît pas au-dessus du clavier ?'}
                  </button>
                  {comment && (
                    <ul
                      style={{ fontSize: 10.5, margin: '4px 0 0', paddingLeft: 16, color: C.grey }}
                    >
                      <li style={{ marginBottom: 3 }}>{AIDE_CLAVIER}</li>
                      <li>
                        <button
                          type="button"
                          onClick={() => champ.current && champ.current.click()}
                          style={{
                            border: 'none',
                            background: 'none',
                            padding: 0,
                            fontSize: 10.5,
                            color: C.teal,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          Ou choisir une fiche contact enregistrée dans Fichiers
                        </button>
                      </li>
                    </ul>
                  )}
                </>
              )}
            </form>
          )}
        </div>
      )}
      <input
        ref={champ}
        type="file"
        accept=".vcf,text/vcard,text/x-vcard"
        aria-label="Fiche contact (.vcf)"
        style={{ display: 'none' }}
        onChange={lit}
      />
    </div>
  );
}
